import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";

import type {
  QuestionnaireResponse,
  ResponseValue,
} from "@/types/questionnaire/form";
import type { Question } from "@/types/questionnaire/question";

interface NumberQuestionProps {
  question: Question;
  questionnaireResponse: QuestionnaireResponse;
  updateQuestionnaireResponseCB: (
    values: ResponseValue[],
    questionId: string,
    note?: string,
  ) => void;
  disabled?: boolean;
  index: number;
}

export function NumberQuestion({
  question,
  questionnaireResponse,
  updateQuestionnaireResponseCB,
  disabled,
  index,
}: NumberQuestionProps) {
  const handleChange = (value: string) => {
    const emptyValue = value === "";
    const numericValue = question.type === "decimal" ? value : parseInt(value);

    const newValues = [...questionnaireResponse.values];
    newValues[index] = {
      type: "number",
      value: emptyValue ? undefined : numericValue,
    };

    updateQuestionnaireResponseCB(
      newValues,
      questionnaireResponse.question_id,
      questionnaireResponse.note,
    );
  };

  const isSlider = question.styling_metadata?.classes?.includes("slider");

  if (isSlider) {
    const currentValue =
      Number(questionnaireResponse.values[index]?.value) || 1;
    const min = 1;
    const max = 10;

    // 10 emojis representing the pain scale from 1 to 10
    const painEmojis = [
      "😀",
      "🙂",
      "😐",
      "😕",
      "😟",
      "😣",
      "😖",
      "😫",
      "😩",
      "😭",
    ];

    return (
      <div className="flex flex-col gap-3 py-2 w-full px-2">
        <div className="flex justify-between items-center w-full relative">
          <Slider
            min={min}
            max={max}
            step={1}
            value={[currentValue]}
            onValueChange={(vals) => handleChange(vals[0].toString())}
            disabled={disabled}
            className="w-full"
            trackClassName="bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 h-2"
          />
        </div>

        <div className="flex justify-between w-full text-lg mt-0.5 select-none relative">
          {painEmojis.map((emoji, i) => {
            const isSelected = currentValue === i + 1;
            return (
              <div
                key={i}
                className={`transition-all duration-200 text-center cursor-pointer ${
                  isSelected
                    ? "scale-125 opacity-100"
                    : "scale-100 opacity-40 hover:opacity-70"
                }`}
                style={{ width: "20px" }}
                onClick={() => {
                  if (!disabled) {
                    handleChange((i + 1).toString());
                  }
                }}
              >
                {emoji}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between text-xs font-medium text-gray-500 px-1">
          <span>{min}</span>
          <span className="font-semibold text-gray-700">
            Selected: {currentValue} {painEmojis[currentValue - 1]}
          </span>
          <span>{max}</span>
        </div>
      </div>
    );
  }

  return (
    <Input
      type="number"
      inputMode={question.type === "decimal" ? "decimal" : "numeric"}
      pattern={"[0-9]*[.]?[0-9]*"}
      value={questionnaireResponse.values[index]?.value?.toString() || ""}
      onChange={(e) => handleChange(e.target.value)}
      step={question.type === "decimal" ? "0.01" : "1"}
      disabled={disabled}
    />
  );
}
